import axios from "./axios";

type UploadKind = "image" | "docx";

type UploadSingleOptions = {
  kind?: UploadKind;
  maxFileSizeBytes?: number;
};

type UploadMultipleOptions = {
  kind?: UploadKind;
  maxFileSizeBytes?: number;
};

const DEFAULT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp", ".gif", ".avif", ".svg"] as const;
const DOCX_EXTENSIONS = [".docx"] as const;

function extractFileName(responseData: unknown): string | null {
  if (!responseData) {
    return null;
  }

  if (typeof responseData === "string") {
    return responseData.trim();
  }

  if (Array.isArray(responseData) && responseData.length > 0) {
    const first = responseData[0];
    return first ? String(first).trim() : null;
  }

  if (typeof responseData === "object") {
    const typed = responseData as { fileName?: string; url?: string };
    if (typed.fileName) {
      return typed.fileName.trim();
    }
    if (typed.url) {
      const value = typed.url.trim();
      const parts = value.split("/");
      return parts[parts.length - 1] ?? value;
    }
  }

  return null;
}

function getFileExtension(fileName: string): string {
  const index = fileName.lastIndexOf(".");
  if (index < 0) {
    return "";
  }
  return fileName.slice(index).toLowerCase();
}

function getApiErrorMessage(error: unknown): string | null {
  const message =
    (error as any)?.response?.data?.message ??
    (error as any)?.response?.data?.error ??
    (error as any)?.message;
  if (typeof message !== "string") {
    return null;
  }
  const trimmed = message.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function validateAssetFile(file: File, kind: UploadKind, maxFileSizeBytes: number): void {
  if (!file || file.size <= 0) {
    throw new Error("Gecerli bir dosya secin.");
  }

  if (file.size > maxFileSizeBytes) {
    const maxMb = (maxFileSizeBytes / (1024 * 1024)).toFixed(0);
    throw new Error(`Dosya boyutu cok buyuk. En fazla ${maxMb} MB yukleyebilirsiniz.`);
  }

  const extension = getFileExtension(file.name);
  if (kind === "image") {
    if (!file.type.startsWith("image/")) {
      throw new Error("Sadece gorsel dosyasi yukleyebilirsiniz.");
    }
    if (extension && !IMAGE_EXTENSIONS.includes(extension as (typeof IMAGE_EXTENSIONS)[number])) {
      throw new Error(`Desteklenmeyen gorsel uzantisi: ${extension}`);
    }
    return;
  }

  if (kind === "docx") {
    if (!DOCX_EXTENSIONS.includes(extension as (typeof DOCX_EXTENSIONS)[number])) {
      throw new Error("Sadece DOCX dosyasi yukleyebilirsiniz.");
    }
    const mime = file.type.toLowerCase();
    if (
      mime &&
      mime !== "application/vnd.openxmlformats-officedocument.wordprocessingml.document" &&
      mime !== "application/octet-stream"
    ) {
      throw new Error("Yuklenen dosya DOCX formatinda olmalidir.");
    }
  }
}

export async function uploadSingleAsset(file: File, options?: UploadSingleOptions): Promise<string> {
  const kind = options?.kind ?? "image";
  const maxFileSizeBytes = options?.maxFileSizeBytes ?? DEFAULT_MAX_FILE_SIZE_BYTES;
  validateAssetFile(file, kind, maxFileSizeBytes);

  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await axios.post("/contentpart/upload", formData);

    const fileName = extractFileName(response.data);
    if (!fileName) {
      throw new Error("Dosya yuklemesi basarisiz: sunucu dosya adini donmedi.");
    }

    return fileName;
  } catch (error) {
    const message = getApiErrorMessage(error);
    throw new Error(message ?? "Dosya yuklenemedi.");
  }
}

export async function uploadMultipleAssets(files: File[], options?: UploadMultipleOptions): Promise<string[]> {
  if (files.length === 0) {
    return [];
  }

  const kind = options?.kind ?? "image";
  const maxFileSizeBytes = options?.maxFileSizeBytes ?? DEFAULT_MAX_FILE_SIZE_BYTES;
  files.forEach((file) => validateAssetFile(file, kind, maxFileSizeBytes));

  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));

  try {
    const response = await axios.post("/contentpart/upload-multiple", formData);

    const data = response.data;
    if (Array.isArray(data) && data.length > 0) {
      return data.map((entry) => String(entry).trim()).filter((entry) => entry.length > 0);
    }

    if (typeof data === "string" && data.trim().length > 0) {
      return [data.trim()];
    }

    throw new Error("Gorsel listesi yuklenemedi.");
  } catch (error) {
    const message = getApiErrorMessage(error);
    throw new Error(message ?? "Gorsel listesi yuklenemedi.");
  }
}
